/**
 * MAHI discovery feed — the swipeable product stack.
 *
 * Reuses the design system established for onboarding (raspberry for
 * product/UI actions, mahiteal reserved for MAHI's own voice, Instrument
 * Serif/Sans, spring motion), but sits on the same pure-white ground as
 * Home rather than the blush wash onboarding uses.
 *
 * Pulls the same /inventory/dresses feed Home.jsx does, filtered to items
 * with at least one photo (a card with nothing to show isn't useful in a
 * swipe/try-on flow) and to the user's own gender setting — defaulting to
 * women's for anyone who hasn't set one, same as the profile's account
 * screen (routes/mira/profile.py owns the setting itself).
 *
 * Also filtered to the categories and price ceiling this person picked
 * (PreferenceSetup, below). This screen owns three first-run surfaces, in a
 * fixed order: "what are you shopping for?", then the category picker, then
 * MAHI's walkthrough. Only the first of them precedes a card. The other two
 * used to as well, which meant a brand-new visitor met a modal and a tour
 * before a single dress — the picker now waits until they have swiped a few,
 * where the question lands on someone who is already engaged and can see what
 * it would change.
 *
 * No deck on this screen ends where its first batch does. useFeedRefill pages
 * the default feed as the shopper runs low, so forty cards is the size of a
 * fetch rather than the size of the catalogue. And a deck MAHI curated (a
 * search, or a tapped category) keeps going too: useDeckContinuation tops it
 * up, asking the backend for the next rung of its widening ladder — the same
 * search paged deeper while it lasts, then progressively broader ones that
 * each keep an anchor from what was asked. Crossing into that broader territory is not
 * silent: DeckTransitionCard puts her line about it at the seam, over the first
 * widened product, and the shopper agrees to it before swiping on.
 *
 * Local state (the saved/passed Sets, the undo stack) still drives the UI
 * optimistically so the gesture never waits on the network, but every swipe is
 * now also persisted via /api/mira/swipes. That history is what the MAHI agent
 * reads to learn someone's taste over time; before it existed, likes and passes
 * were lost on reload.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import mahi, { recordSwipe, trackEvent, undoSwipe } from '../../api';
import { getSessionId } from '../../analytics';
import { useMahiAuth } from '../../auth';
import { feedGenderFor, fetchFreshFeed, getPrefetchedFeed } from '../../prefetch';
import { prefsFromUser, prefsSignature } from '../../discoveryPrefs';
import { sessionSeenSkus, shouldShowNotice, markNoticeShown } from './discoverySession';
import ProductSwipeCard from './ProductSwipeCard';
import ProductDetailView from './ProductDetailView';
import WardrobeButton from '../wardrobe/WardrobeButton';
import { DislikeMark, LikeMark } from '../ui/marks';
import MahiHeader from '../ui/MahiHeader';
import MahiAskButton from './MahiAskButton';
import useAskBeat from './useAskBeat';
import MahiChatPanel from './MahiChatPanel';
import MahiResultsChip from './MahiResultsChip';
import MahiResultsCascade from './MahiResultsCascade';
import DiscoveryTour from './DiscoveryTour';
import PreferenceSetup from './PreferenceSetup';
import ShoppingForSetup from './ShoppingForSetup';
import DeckTransitionCard from './DeckTransitionCard';
import useDeckContinuation from './useDeckContinuation';
import useFeedRefill from './useFeedRefill';
import useDiscoverTour from './useDiscoverTour';
import useShoppingFor from './useShoppingFor';
import useDiscoveryPrefs from './useDiscoveryPrefs';
import useMahiConversation from './useMahiConversation';
import { createLiveTransport } from './mahiLiveTransport';
import ProfilePanel from '../profile/ProfilePanel';

// The orb, the open chat panel, and the collapsed results chip are three
// mutually-exclusive representations of one entity — this is the id that
// lets framer-motion FLIP between whichever of them is currently mounted,
// wherever in the DOM it happens to live (bottom action row, fixed overlay,
// or the header slot above the card stage).
const MAHI_SURFACE_LAYOUT_ID = 'mahi-conversation-surface';

// Below this many unseen cards left in the default feed, coming back to it
// fetches a new one rather than handing over the tail of the old. Six is the
// point at which the stack (three rendered, one swiped every couple of
// seconds) would visibly run out before a fetch triggered by running low could
// land — see useDeckContinuation's REFILL_AT, which is the same reasoning.
const MIN_DEFAULT_DECK = 6;

// How many cards someone swipes before MAHI asks what they want to see more
// of. The question used to be the first thing on screen, ahead of any product;
// six cards in, it is asked of someone who has seen what the feed holds and
// can judge the answer, and it costs nothing at the moment that matters most
// — the first few seconds after landing.
const PREFS_AFTER_SWIPES = 6;

export default function DiscoveryFeed({ introPending = false }) {
  const { user, loading: authLoading } = useMahiAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [exitDirection, setExitDirection] = useState(null);
  // Saved/passed skus are kept only to drive undo; neither is rendered, so a
  // ref each is enough and avoids a re-render on every swipe. (`savedSkus` was
  // state until the wardrobe button replaced the count pill it fed — the
  // badge now reads `savedCount`, seeded from the server.)
  const savedSkusRef = useRef(new Set());
  const passedSkusRef = useRef(new Set());
  // Every sku this session has had a verdict on, across every deck — the
  // default feed and any MAHI search alike. Distinct from the two Sets above,
  // which exist only to drive Undo: this one is what stops a deck being
  // rebuilt out of cards the shopper has already swiped. Backed by the
  // module-scope Set in discoverySession.js rather than a fresh one per
  // mount — leaving /discover for the wardrobe or profile and coming back
  // remounts this component, and a ref that started empty again on every
  // visit was exactly what let a deck replay cards the shopper had just
  // swiped past.
  const seenSkusRef = useRef(sessionSeenSkus);
  const [history, setHistory] = useState([]); // [{ sku, direction, prevIndex }]
  const [detailProduct, setDetailProduct] = useState(null);
  // The top card's own try-on result image, reported up from ProductSwipeCard
  // (see onTryOnUpdate below) purely so the full-screen photo viewer has it
  // to open on when the card is tapped — this screen doesn't display
  // anything else about the try-on itself (see Style Studio for that).
  const [topTryOn, setTopTryOn] = useState(null); // { imageUrl } | null
  const handleTopTryOnUpdate = useCallback((update) => {
    setTopTryOn((prev) => (prev?.imageUrl === update.imageUrl ? prev : update));
  }, []);
  // A new card taking over the top spot starts with nothing to report yet.
  useEffect(() => { setTopTryOn(null); }, [index]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [saveTargetOffset, setSaveTargetOffset] = useState(null);
  const wardrobeButtonRef = useRef(null);
  const cardStageRef = useRef(null);

  // Stop point (b): the real agent, streamed over SSE — see mahiLiveTransport.js.
  // mahiMockTransport.js (same interface) is kept around for local dev
  // without a backend/login, like when this feature was first built.
  const transport = useMemo(() => createLiveTransport(), []);
  const [cascadeProducts, setCascadeProducts] = useState(null);
  const defaultItemsRef = useRef([]);

  // How the curated deck currently on screen keeps going once these products
  // run out — `{ conversationId }` for a search, `{ categories }` for a tapped
  // category, null for the default feed (which has no request to stay anchored
  // to). See useDeckContinuation.
  const [deckSource, setDeckSource] = useState(null);
  const pendingSourceRef = useRef(null);
  // Mirrors `deckSource` for code that has to read it after an await. State
  // read inside an async callback is the value captured when that callback was
  // created, which for a deck that can be replaced mid-flight is the one thing
  // it must not be.
  const deckSourceRef = useRef(null);
  // Invalidates an in-flight default-feed fetch: bumped whenever the deck is
  // replaced, so a reply that lands after the shopper has moved on is dropped
  // instead of overwriting what they moved on to.
  const restoreTokenRef = useRef(0);
  // Bumped on every handoff so a refine — which replaces the whole stack while
  // keeping the same conversation id — reads as the new deck it is, rather
  // than a continuation of the deck it replaced.
  const [deckKey, setDeckKey] = useState(0);

  const handleMahiHandoff = useCallback((products, meta) => {
    // Held rather than applied: the cascade is still playing over the old deck,
    // and swapping the source now would have the continuation hook topping up a
    // deck that is about to be replaced.
    pendingSourceRef.current = meta?.source || null;
    setCascadeProducts(products);
  }, []);

  const handleCascadeComplete = useCallback(() => {
    setItems(cascadeProducts || []);
    deckSourceRef.current = pendingSourceRef.current;
    restoreTokenRef.current += 1;
    setDeckSource(pendingSourceRef.current);
    setDeckKey((k) => k + 1);
    setIndex(0);
    setHistory([]);
    setCascadeProducts(null);
  }, [cascadeProducts]);

  /**
   * Closing a MAHI search and coming back to the default feed.
   *
   * This used to be `setItems(defaultItemsRef.current); setIndex(0)` — the
   * original deck, rewound to card one. So someone who swiped through fifteen
   * dresses, asked MAHI for something, then dismissed her was handed those
   * same fifteen dresses again, in the same order, to swipe past a second
   * time. The search was punished with a rewind.
   *
   * What comes back instead is what they have *not* seen: the same deck minus
   * everything this session has had a verdict on, in either the default feed
   * or the search they were just in. Position is not restored, because after a
   * detour through a search "carry on where I was" and "show me something new"
   * are the same request — and the remainder is already in the order the feed
   * chose.
   *
   * When too little of it survives to be a deck, a genuinely new one is
   * fetched. That goes to the live route rather than the cached prefetch: the
   * cache would replay the deck they just worked through, and the API knows
   * what they have swiped across every session, not just this one.
   */
  const restoreDefaultDeck = useCallback(() => {
    const unseen = defaultItemsRef.current.filter(
      (p) => !seenSkusRef.current.has(p.mira_sku));
    setItems(unseen);
    deckSourceRef.current = null;
    pendingSourceRef.current = null;
    setDeckSource(null);
    setIndex(0);
    setHistory([]);

    if (unseen.length >= MIN_DEFAULT_DECK) return;
    const token = (restoreTokenRef.current += 1);
    (async () => {
      const genderKey = user?.gender === 'male' ? 'male' : 'female';
      const { items: fetched } = await fetchFreshFeed(genderKey);
      // Dropped if the deck moved on while this was in flight — they may have
      // asked MAHI for something else, and replacing that with the feed would
      // undo their request.
      if (restoreTokenRef.current !== token || deckSourceRef.current) return;
      // Still filtered locally: the route excludes what the *server* knows
      // they have swiped, and a verdict from thirty seconds ago may not have
      // landed there yet.
      const fresh = (fetched || []).filter((p) => !seenSkusRef.current.has(p.mira_sku));
      if (!fresh.length) return;
      defaultItemsRef.current = fresh;
      setItems(fresh);
      setIndex(0);
      setHistory([]);
    })();
  }, [user?.gender]);

  const handleMahiClear = restoreDefaultDeck;

  // Appended, never replaced: the cards behind the shopper are what Undo walks
  // back through, and `index` is a position in this same array.
  const handleDeckAppend = useCallback((more) => {
    setItems((current) => {
      const known = new Set(current.map((p) => p.mira_sku));
      return [...current, ...more.filter((p) => !known.has(p.mira_sku))];
    });
  }, []);

  // The same append for the default feed, plus the one thing a curated deck
  // does not need: the topped-up cards join `defaultItemsRef` too, so coming
  // back from a MAHI search restores the feed as it had grown rather than as
  // it was fetched at mount.
  const handleDefaultAppend = useCallback((more) => {
    const known = new Set(defaultItemsRef.current.map((p) => p.mira_sku));
    defaultItemsRef.current = [
      ...defaultItemsRef.current, ...more.filter((p) => !known.has(p.mira_sku))];
    handleDeckAppend(more);
  }, [handleDeckAppend]);

  const conversation = useMahiConversation({
    user,
    transport,
    onHandoff: handleMahiHandoff,
    onClear: handleMahiClear,
  });

  // Chat history's two actions land here: navigate('/discover', {
  // state: { restoreConversation | resumeConversation } }). Read once, then
  // scrubbed from history state so a later refresh doesn't replay it.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const { restoreConversation, resumeConversation } = location.state || {};
    if (!restoreConversation && !resumeConversation) return;
    if (restoreConversation) conversation.restoreFromHistory(restoreConversation);
    else conversation.resumeFromHistory(resumeConversation);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Measured rather than guessed: the offset from the card's centre to the
  // button's centre depends on the viewport, and a hardcoded vector would send
  // the card past the button on a tablet and short of it on a small phone.
  const measureSaveTarget = useCallback(() => {
    const button = wardrobeButtonRef.current?.getBoundingClientRect();
    const stage = cardStageRef.current?.getBoundingClientRect();
    if (!button || !stage) return null;
    return {
      x: (button.left + button.width / 2) - (stage.left + stage.width / 2),
      y: (button.top + button.height / 2) - (stage.top + stage.height / 2),
    };
  }, []);

  // What this account asked to see, and a stable key for it. The key is a
  // dependency of the feed fetch below because the deck is built from these:
  // saving a new set of picks has to produce a new deck, and neither `gender`
  // nor anything else already in that dependency list changes when they do.
  const prefs = useMemo(() => prefsFromUser(user), [user]);
  const prefsKey = prefsSignature(prefs);

  useEffect(() => {
    // Same gate Home.jsx uses — wait for auth to resolve so the gender
    // filter is applied on the first fetch instead of an ungated fetch
    // followed by a second one once gender is known.
    if (authLoading) return;
    (async () => {
      try {
        // Always sent now, defaulting to women's for anyone who hasn't set a
        // gender — the same default the profile's own account screen shows
        // pre-selected. Previously this only sent a filter when gender was
        // already set, which is what left the feed unfiltered (in practice
        // reading as "hardcoded to women's" purely from catalog size) for
        // every user until they had a reason to visit settings.
        const genderKey = feedGenderFor(user);
        // Warmed at app boot for a returning user, and at OTP/Google/onboarding
        // for a new one, so by the time this runs it is usually already
        // resolved and /discover shows dresses with no spinner. prefetch.js
        // owns the whole ladder — CDN snapshot first, live /inventory/dresses
        // behind it — and starts the fetch itself if nothing warmed it, so
        // there is no cold path left to duplicate here. Null means both tiers
        // failed, which is the toast below.
        const { items: fetched, notice } = await getPrefetchedFeed(genderKey, prefs);
        if (!fetched) throw new Error('feed unavailable');
        defaultItemsRef.current = fetched;
        // Same filter restoreDefaultDeck applies when a search hands the
        // shopper back to this feed: what comes back is what they have not
        // seen. Without it, leaving /discover and returning replayed the
        // cached deck's own front cards — cards this same session had
        // already had a verdict on — from position one.
        setItems(fetched.filter((p) => !seenSkusRef.current.has(p.mira_sku)));
        // MAHI's line about the deck she just built — that she has widened
        // past the categories they picked, or had nothing in them at all. Said
        // once, in her voice, rather than silently serving something other
        // than what was asked for; that quiet substitution is the thing that
        // erodes trust in a filter. shouldShowNotice is what makes "once"
        // mean once per session rather than once per mount — this same
        // notice used to fire again every time the shopper revisited the
        // screen with the underlying pool still thin.
        if (shouldShowNotice(notice)) {
          toast(notice, { id: 'feed-notice', duration: 6000 });
          markNoticeShown(notice);
        }
        // The cards are ready — show them. This used to live in a `finally`
        // after the wardrobe count below, which meant the whole stack sat
        // behind a spinner for the length of a request that only decides a
        // number on a badge. Nothing about the deck depends on that count.
        setLoading(false);
      } catch {
        toast.error('Could not load dresses');
        setLoading(false);
      }
    })();
  }, [authLoading, user?.gender, user?.shopping_for, prefsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The buy-click redirect bounced someone back here because it could not
  // send them to the merchant — an expired token, a delisted product, or a
  // domain that is not on the allowlist (see routes/redirect.py). Landing
  // silently back on the feed makes a tapped Buy Now look like it did
  // nothing, so say so once and clean the URL up.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('unavailable')) return;
    toast("That link isn't working right now - I'll take another look at it.",
      { id: 'buy-unavailable' });
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Wardrobe badge — seeded from the server, not from this session's swipes,
  // so it is right on a fresh load rather than starting at zero every time.
  // Its own effect, running alongside the feed rather than queued behind it:
  // the deck must never wait on a number in the corner of the screen.
  useEffect(() => {
    if (authLoading) return;
    mahi.get('/wardrobe/count')
      .then(({ data }) => setSavedCount(data.count || 0))
      .catch(() => { /* the badge is not worth failing the feed over */ });
  }, [authLoading]);

  const deck = items;
  const current = deck[index];
  const isEmpty = index >= deck.length;

  // Tops the deck up before the shopper can reach the end of it, and reports
  // the one moment worth interrupting for: when the top-up had to widen past
  // what they actually asked for.
  const { transition, dismissTransition } = useDeckContinuation({
    source: deckSource, deckKey, deck, index, onAppend: handleDeckAppend,
  });

  // And the same for the deck that has no curation behind it. Without this the
  // default feed was forty cards and then "You're all caught up" — the exact
  // wall useDeckContinuation was built to remove from searches, still standing
  // on the screen every session starts on.
  useFeedRefill({
    active: !deckSource && !loading,
    deck,
    index,
    gender: user?.gender === 'male' ? 'male' : 'female',
    prefsKey,
    seenRef: seenSkusRef,
    onAppend: handleDefaultAppend,
  });
  // The seam is a position in the deck, so it survives Undo walking back
  // across it — swipe forward again and the same offer is where it was.
  const atTransition = Boolean(transition) && index === transition.atIndex;

  // When the "Ask MAHI" lockup ripples. `index` is the activity signal — it
  // changes on every swipe and on every Undo, so the idle timer restarts
  // whenever the shopper is doing anything at all. The thin-deck trigger is
  // the same threshold the default feed tops up at, so the invitation to talk
  // to her arrives while there are still cards left rather than at the wall.
  const askBeat = useAskBeat({
    activityKey: index,
    deckThin: isEmpty || atTransition || deck.length - index <= MIN_DEFAULT_DECK,
    enabled: conversation.phase === 'orb',
  });

  // product.impression / product.dwell (product doc Section 4) — a card only
  // counts as "seen" once it's actually the top of the stack, not while it's
  // one of the two peeking behind it. Keyed on mira_sku rather than `index`
  // so a MAHI handoff/clear that resets `index` to 0 onto a *different* sku
  // still fires a fresh impression instead of being a no-op dependency change.
  const dwellStartRef = useRef(null);
  const dwellSkuRef = useRef(null);
  useEffect(() => {
    const prevSku = dwellSkuRef.current;
    const prevStart = dwellStartRef.current;
    if (prevSku && prevStart) {
      trackEvent('product.dwell', { duration_ms: Date.now() - prevStart },
        { sessionId: getSessionId(), surface: 'discover', productId: prevSku });
    }
    // Not while the transition card is covering it — a card nobody can see is
    // not an impression, and counting one would quietly inflate the exact
    // metric this whole path is meant to be judged on. The impression fires
    // for real the moment the offer is dismissed.
    if (current?.mira_sku && !atTransition) {
      dwellSkuRef.current = current.mira_sku;
      dwellStartRef.current = Date.now();
      trackEvent('product.impression', {},
        { sessionId: getSessionId(), surface: 'discover', productId: current.mira_sku });
    } else {
      dwellSkuRef.current = null;
      dwellStartRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.mira_sku, atTransition]);

  // MAHI's first-landing walkthrough. Gated on a real card being on screen —
  // she points at the card and the try-on button, and neither exists while the
  // feed is still loading or once the deck has run out.
  //
  // Also gated on the session-entry animation being finished (`introPending`,
  // passed down from pages/Discover). Both fire on a first landing and the
  // intro paints over the whole screen, so without this her opening step —
  // which flies her to the middle of the screen to say hello — would play out
  // behind it, and she would simply be standing there when the wordmark lands.
  // First-run surfaces, in order: the wordmark intro, then the preference
  // picker, then MAHI's walkthrough. They are sequenced rather than raced
  // because each is about the one after it — the picker decides what is in the
  // card stack, and the tour points at that stack. `prefsSettled` (rather than
  // `!prefsActive`) is what the tour waits on, so it also stays put through
  // the frame before the modal has decided to open, and after a picker that
  // failed to load and gave up for the session.
  //
  // "What are you shopping for?" comes first and is the only one of them that
  // can precede a loaded deck — it decides which half of the catalogue the
  // deck is drawn from, so waiting for cards before asking would mean building
  // a deck we are about to discard. Hence `ready` here omits `!loading`.
  const { shoppingForActive, shoppingForSettled, finishShoppingFor } = useShoppingFor({
    user, authLoading, ready: !introPending,
  });

  const { prefsActive, prefsSettled, finishPrefs } = useDiscoveryPrefs({
    user, authLoading,
    ready: !loading && !introPending && shoppingForSettled && !shoppingForActive
      && history.length >= PREFS_AFTER_SWIPES,
  });

  const { tourActive, finishTour, replayTour } = useDiscoverTour({
    user, authLoading,
    ready: !loading && !isEmpty && !introPending && !shoppingForActive
      && prefsSettled && !prefsActive,
  });

  const commitSwipe = (direction) => {
    const sku = current.mira_sku;
    setHistory((h) => [...h, { sku, direction, prevIndex: index }]);
    seenSkusRef.current.add(sku);

    if (direction === 'right') {
      savedSkusRef.current.add(sku);
      recordSwipe(sku, 'like');
      toast.success('Saved to Wardrobe', { id: `saved-${sku}` });
    } else if (direction === 'left') {
      passedSkusRef.current.add(sku);
      recordSwipe(sku, 'pass');
    }

    setIndex((i) => i + 1);
    setExitDirection(null);
  };

  // Trying something on is not a swipe — it lives entirely on the card
  // itself now (see ProductSwipeCard's useTryOnJob), which replaces its own
  // photo in place once the result lands rather than taking over the screen.
  // This only ever gets 'left'/'right' verdicts, both of which advance the deck.
  const handleSwipe = (direction) => {
    if (exitDirection || isEmpty) return;
    // A verdict cast at the seam is agreement with the offer, not a verdict on
    // the card behind it: dismiss and let them swipe the actual product. Never
    // a dead end — the only thing this card can do is let the deck continue.
    if (atTransition) { dismissTransition(); return; }
    // Measured at swipe time, not on mount — the top bar's height changes once
    // the badge appears, which moves the button by a few pixels.
    setSaveTargetOffset(direction === 'right' ? measureSaveTarget() : null);
    setExitDirection(direction);
    // The card lands in the button before the badge ticks, so the number
    // increments *because* of the card arriving rather than alongside it.
    if (direction === 'right') setTimeout(() => setSavedCount((c) => c + 1), 380);
    setTimeout(() => commitSwipe(direction), direction === 'right' ? 460 : 300);
  };

  const handleTap = () => {
    if (exitDirection || isEmpty || atTransition) return;
    // Once a try-on result is showing, that's what's actually on screen —
    // tapping it should open full-screen on that image first, with the
    // dress's own photos still a swipe away rather than gone.
    const images = topTryOn?.imageUrl
      ? [topTryOn.imageUrl, ...(current.image_urls || [])]
      : current.image_urls;
    setDetailProduct({ ...current, image_urls: images });
  };

  const handleUndo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setHistory((h) => h.slice(0, -1));
    savedSkusRef.current.delete(last.sku);
    passedSkusRef.current.delete(last.sku);
    // Taken back here too, or a card they undid would be treated as seen and
    // quietly dropped the next time the default deck is rebuilt.
    seenSkusRef.current.delete(last.sku);
    if (last.direction === 'right') setSavedCount((c) => Math.max(0, c - 1));
    // Retract the stored swipe too — leaving it would teach the profile
    // something the user explicitly took back.
    undoSwipe(last.sku);
    setIndex(last.prevIndex);
    toast.dismiss();
    toast('Undone', { icon: '↩︎' });
  };

  // Keyboard fallback for desktop testing — same handler as the buttons/drag.
  // Skipped entirely while the full-screen photo viewer is open — it has
  // its own Left/Right/Escape handling, and without this guard both
  // listeners would fire on the same keypress (the card swiping behind
  // the modal while you're just trying to browse photos).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (detailProduct) return;
      if (e.key === 'ArrowLeft') handleSwipe('left');
      else if (e.key === 'ArrowRight') handleSwipe('right');
      else if (e.key.toLowerCase() === 'z' && (e.metaKey || e.ctrlKey)) handleUndo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitDirection, isEmpty, index, history, detailProduct]);

  return (
    <div className="fixed inset-0 bg-white text-slate-900 antialiased flex flex-col">
      {/* top bar — the shared masthead (components/ui/MahiHeader), the same
          row every MAHI screen opens with. Discover passes its own logo
          click handler: clearing the active MAHI chip/filter and resetting
          the feed, not just navigating to a page it's already on (the
          default "go to Discover" behavior every other page uses). */}
      <MahiHeader
        onLogoClick={() => conversation.clearQuery()}
        left={(
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            data-testid="discover-profile-avatar"
            aria-label="Your profile"
            className="w-9 h-9 rounded-full bg-white ring-1 ring-slate-200 overflow-hidden flex items-center justify-center shrink-0 active:scale-90 transition-transform"
          >
            {user?.picture
              ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
              : <span className="text-xs font-semibold text-slate-500">{(user?.name || 'U').slice(0, 1).toUpperCase()}</span>}
          </button>
        )}
        right={<WardrobeButton ref={wardrobeButtonRef} count={savedCount} />}
      />

      {/* Header slot for the results chip — only occupied once a MAHI
          conversation has actually produced a curated deck. Her intro line
          (MahiResultsChip's `caption` prop) is deliberately not passed here:
          on the phone-width screen this is designed for, that extra sentence
          plus the chip together ate a real slice of the one thing this
          screen exists to show — the product card — pushing it visibly
          smaller every time a search was active. The chip alone (what she
          searched for, reopen, clear) carries the useful information. */}
      <AnimatePresence>
        {conversation.phase === 'chip' && conversation.query && (
          <MahiResultsChip
            layoutId={MAHI_SURFACE_LAYOUT_ID}
            label={conversation.query.label}
            onReopen={conversation.reopenFromChip}
            onClear={conversation.clearQuery}
          />
        )}
      </AnimatePresence>

      {/* card stack — fixed 3:4 aspect ratio, width-driven (not "stretch to
          fill whatever flex space is left" — that made the crop depend on
          however much height the top bar/action row happened to leave on
          a given phone, which is what read as "stretched"). Capped at
          max-w-sm so it doesn't become an oversized card on tablet/desktop. */}
      <div className="flex-1 flex items-center justify-center px-3 pb-3 min-h-0 overflow-hidden">
        <div ref={cardStageRef} data-tour="card" className="relative w-full max-w-sm aspect-[3/4] max-h-full mx-auto rounded-[1.75rem] shadow-[0_30px_70px_-25px_rgba(15,23,42,0.35)]">
          {cascadeProducts ? (
            <MahiResultsCascade products={cascadeProducts} onComplete={handleCascadeComplete} />
          ) : loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : isEmpty ? (
            /* Reachable, but no longer cheap. A curated deck now continues
               past its exact matches and widens along the ladder in
               services/mahi/widen.py before it can land here, so this is what
               a genuinely exhausted search looks like rather than what eight
               products looked like — and it offers a way out either way,
               because "there is nothing" with nothing to tap is how someone
               leaves. */
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 bg-white">
              <Sparkles className="w-10 h-10 text-raspberry-400 mb-4" />
              <h3 className="font-mahi-serif text-2xl text-slate-900 mb-2">
                {deckSource ? "That's everything close to that" : "You're all caught up"}
              </h3>
              <p className="text-sm text-slate-500 font-mahi-sans max-w-xs">
                {deckSource
                  ? "I've shown you everything I have for that hunt, and everything near it. Tell me what else you're after and I'll go again."
                  : "Tell MAHI what you're after and she'll bring fresh picks."}
              </p>
              {deckSource && (
                <button
                  type="button"
                  onClick={conversation.clearQuery}
                  data-testid="empty-back-to-feed"
                  className="mt-6 px-6 py-2.5 rounded-full bg-white ring-1 ring-slate-200 shadow-sm text-[14px] font-medium text-slate-700 font-mahi-sans active:scale-95 transition-transform"
                >
                  Back to my feed
                </button>
              )}
            </div>
          ) : (
            <>
              {deck.slice(index, index + 3).map((product, i) => (
                <ProductSwipeCard
                  key={product.mira_sku}
                  product={product}
                  isTop={i === 0 && !atTransition}
                  stackIndex={i}
                  onSwipe={handleSwipe}
                  onTap={handleTap}
                  onTryOn={() => recordSwipe(product.mira_sku, 'tryon')}
                  onTryOnUpdate={handleTopTryOnUpdate}
                  exitDirection={i === 0 ? exitDirection : null}
                  saveTargetOffset={i === 0 ? saveTargetOffset : null}
                />
              ))}
              {/* Over the stack, not instead of it: the first widened product
                  is already visible behind the offer, so what she is offering
                  and the thing itself are on screen together. */}
              <AnimatePresence>
                {atTransition && (
                  <DeckTransitionCard
                    key="deck-transition"
                    message={transition.message}
                    onContinue={dismissTransition}
                    onAskMahi={() => { dismissTransition(); conversation.openPanel(); }}
                  />
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>

      {/* MAHI's one-line reaction to a completed try-on used to render here,
          below the card and above her orb. Pulled off this screen entirely —
          on a phone, that extra row (plus its enter/exit animation nudging
          the whole stack) was one more thing competing with the card for a
          screen that's already tight on height. The line still gets
          generated and persisted the moment a try-on completes (see
          ProductSwipeCard's fetch-and-persist effect) — it now surfaces once,
          properly, in Style Studio's detail view, which has the room for it
          and is where someone goes specifically to look back at a result. */}

      {/* Undo lives in normal document flow, its own row between the card
          and the bottom action row — not floating (fixed or absolute) over
          either one. A floating pill has to guess how much gap actually
          exists between the two, and that gap isn't a fixed size: it depends
          on however much vertical space is left after a real phone's browser
          chrome takes its share, so any guessed offset eventually lands on
          top of the card's own buttons or the row below on some real device,
          which is exactly what happened. Taking real space here instead
          means overlap is ruled out by the layout itself rather than by a
          number tuned against one screenshot.

          The row is now always mounted at a fixed height and only the pill
          inside it fades. It used to animate its own height 0 → auto, which
          did save ~44px while Undo was hidden — but the card stage above is
          `flex-1` with `aspect-[3/4] max-h-full`, so every one of those
          height changes resized the card itself. Reserving the space
          unconditionally costs a fixed sliver of a phone screen and buys a
          card that never moves. */}
      <div className="shrink-0 h-11 flex items-center justify-center">
        <AnimatePresence>
          {history.length > 0 && !isEmpty && (
            <motion.button
              key="undo-row"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              type="button"
              onClick={handleUndo}
              data-testid="undo-button"
              className="text-[11px] font-medium text-slate-500 bg-white ring-1 ring-slate-200 shadow-sm rounded-full px-3 py-1.5 font-mahi-sans"
            >
              ↩︎ Undo
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* bottom action row — down to three items now that Try It On lives
          on the card itself. Pass and Wardrobe are small matched quiet
          circles mirroring the left=pass/right=save swipe directions;
          the "Ask MAHI" lockup is the single, dominant, raised entry point
          to her, with identical (small-circle) neighbors on both sides, so
          one uniform gap keeps it symmetric. Its raise is tied to this row's
          `pt-3` — see RAISE in MahiAskButton, which exists to keep it out of
          the Undo pill's row. Changing `pt-3` here means changing that. */}
      {/* The row itself always renders now — the empty state explicitly
          invites "tell MAHI more," so Ask MAHI has to stay reachable even
          with nothing left to swipe; pass/save just have nothing to act on
          then, so only those two hide. */}
      <div className="relative shrink-0 pb-6 pt-3 flex items-center justify-center gap-8">
        {/* Also hidden at the seam: the transition card carries its own two
            actions, and offering "not for me" against a question is a verdict
            on nothing. */}
        {!isEmpty && !atTransition && (
          <button type="button" onClick={() => handleSwipe('left')} data-testid="action-pass"
            aria-label="Not for me"
            className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-400 hover:text-slate-600 active:scale-95 transition-transform">
            <DislikeMark className="w-full h-full" />
          </button>
        )}

        {/* "Ask MAHI" — idle representation of the conversation surface.
            While the panel is open, or a curated deck has produced a
            results chip, this unmounts and the shared layoutId lives on
            whichever of those is currently showing instead. */}
        <AnimatePresence>
          {conversation.phase === 'orb' && (
            <MahiAskButton
              layoutId={MAHI_SURFACE_LAYOUT_ID}
              onOpen={conversation.openPanel}
              beat={askBeat}
            />
          )}
        </AnimatePresence>

        {!isEmpty && !atTransition && (
          <button type="button" onClick={() => handleSwipe('right')} data-testid="action-save"
            aria-label="Add to wardrobe"
            className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center text-raspberry-600 active:scale-95 transition-transform">
            {/* Its own heart mark, not the wardrobe glyph — "like" is a
                distinct verb from the wardrobe it happens to save into. */}
            <LikeMark className="w-full h-full" />
          </button>
        )}
        {/* "For brands" entry point — muted so it doesn't compete with the
            action row, and opened in a new tab so brand reps don't need a MAHI
            account to find it.

            Anchored to this row rather than to the viewport. It used to be
            `fixed bottom-4 left-4`, which put it in the same horizontal band as
            the buttons above: the row's content is 232px wide (44 + 32 + 80 +
            32 + 44) and centred, so it starts at x=79 on a 390px screen but
            x=64 on a 360px one — while the link itself runs from x=16 to about
            x=68. On any phone at or below ~375px that overlapped the Pass
            button, and since the link carried `z-20` it painted *over* it, so
            tapping the left edge of Pass opened the partner site instead.
            Living inside the row's own bottom padding means the two can never
            share space again regardless of screen width or what `pb-6` becomes
            later. */}
        <a
          href="https://partners.mahi.style"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute left-4 bottom-1 text-[10px] text-slate-400 hover:text-slate-600 font-mahi-sans transition"
        >
          For brands
        </a>
      </div>

      <AnimatePresence>
        {detailProduct && (
          <ProductDetailView key={detailProduct.mira_sku} product={detailProduct} onClose={() => setDetailProduct(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {profileOpen && (
          <ProfilePanel onClose={() => setProfileOpen(false)} onReplayTour={replayTour} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {conversation.phase === 'panel' && (
          <MahiChatPanel
            layoutId={MAHI_SURFACE_LAYOUT_ID}
            messages={conversation.messages}
            onSend={conversation.sendMessage}
            onBrowseCategory={conversation.browseCategory}
            browsing={conversation.browsing}
            onClose={conversation.closePanel}
            gender={feedGenderFor(user)}
          />
        )}
      </AnimatePresence>

      {/* Screen readers get her messages announced as they complete, not
          per streamed token — hoisted here, above the panel's own mount
          lifecycle, so an announcement fired right as the panel collapses
          into a chip (the handoff) isn't clipped by the panel unmounting. */}
      {/* Mounted last so it sits above every panel without needing to outbid
          them on z-index beyond its own z-[70]. Keyed by user so a replay from
          the profile panel restarts it from step one rather than resuming
          wherever the previous run stopped. */}
      {/* Mounted before the tour so it paints beneath nothing else, and so
          the tour — which is gated on this having settled — can never start
          behind it. */}
      {/* Ahead of both the picker and the tour, and covering the whole screen:
          until this is answered there is no correct deck to show behind it. */}
      <AnimatePresence>
        {shoppingForActive && (
          <ShoppingForSetup key="shopping-for-setup" onDone={finishShoppingFor} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {prefsActive && (
          <PreferenceSetup key="preference-setup" onDone={finishPrefs} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {tourActive && (
          <DiscoveryTour key="discovery-tour" user={user} onDone={finishTour} />
        )}
      </AnimatePresence>

      <div aria-live="polite" className="sr-only">{conversation.announcement}</div>
    </div>
  );
}
